import React from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import { Link } from 'react-router-dom';

const TeamHealthChecksStage3 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 3: Collect Insights and Share Findings" icon={<ChartBarIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Collect insights and theme the data</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Share findings in an experiment roadmap</span>
            </li>
          </ul>
          <p className="mt-4">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Action: Collect Insights</h3>
        <p className="text-gray-200 mb-4">
          Ok so you've now got a bunch of boards filled in and you're trying to make sense of it.
        </p>

        <p className="text-gray-200 mb-4">
          The first step is to just look at the dot votes and build a quick heatmap view that is kinda like an executive summary of team health:
        </p>

        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/team-health-check-grid.png" alt="Team health heatmap" />

        <ExpandableCallout type="info" title="Note on trends:" className="mt-4">
          <p className="text-gray-200">
            Once you've run this process twice (i.e. done a full team health check twice) you can also add in little trend arrows as above to measure whether certain actions by team or culture experiments initiated by you have made an impact. We'll cover more about that in the next stage <Link to="/playbooks/team-health-checks/stage-4" className="text-primary hover:text-primary-light">Stage 4: Create accountability and momentum with transparency</Link>
          </p>
        </ExpandableCallout>

        <p className="text-gray-200 mt-4 mb-4">
          The next step is to collect improvement ideas that fall outside the team. Here's an example:
        </p>

        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/sticky-note-arrows.png" alt="Improvement ideas collection" />

        <ExpandableCallout type="info" title="Facilitation tip:" className="mt-4">
          <p className="text-gray-200">
            This can take a few hours honestly. You can lighten the load by inviting facilitators from the previous step to help you do this for each of their sessions so your role is to synthesize and theme vs extract.
          </p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Action: Share Findings</h3>
        <p className="text-gray-200 mb-4">
          If you are a hierarchical organisation or have some egos that are prone to bruising you might want to communicate the learnings to different audiences: executives + senior leadership, individual teams etc. I don't personally advocate this but have worked in orgs where not doing this would have caused more trouble than worth. My preferred approach is to create a single transparent view via a (notion) memo, with the following sections and invite comment async:
        </p>

        <CopyToNotion title="Summary of Team Health Check Findings">
          <div className="space-y-4">
            <ol className="list-decimal pl-6 space-y-2">
              <li>Re-cap on the process that you followed and share screenshot/gif/video of the miro board</li>
              <li>Look horizontally across your dashboard view and summarise the key improvement themes that all teams have raised.</li>
              <li>Highlight any particular callouts where teams need extra support</li>
              <li>Highlight any particular insights on either 'things going awesome' or 'things in dumpster fire'</li>
              <li>Summarise the key actions being taken forward by each team (1-2)</li>
              <li>Summarise any key actions that fall within your own remit of people and culture.</li>
              <li>Summarise the key actions/decisions required from leadership</li>
            </ol>
          </div>
        </CopyToNotion>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Recap</h3>
        <p className="text-gray-200 mb-4">
          By the end of this stage you should have:
        </p>
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Consolidated all the findings from running your first round of team health checks across the org</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Published the findings so that everyone has visibility on what's working and what isn't and where the opportunities lie within each team and across all teams</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Started to think about how this might inform your own People & Culture Roadmap</span>
          </li>
        </ul>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default TeamHealthChecksStage3; 